import React, {
  useState, useEffect, useCallback, useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslatedText } from '../../OracleCommons/StaticTextContext'
import type { TarotPhase } from './tarotTheaterMachine'
import { useTarotStageAnimation, CardScene } from './useTarotStageAnimation'
import {
  Rect,
  getFanLayout,
  getFanOffset,
  getPickedLiftOffset,
  getFlyingOffset,
  getShuffleOffset,
  getJiggleRotateY,
  getLandingOffset,
  easeOut,
  easeInOut,
  clamp01,
  lerp,
  FLAY_MS,
  HOVER_MS,
  PICK_MS,
  HANG_MS,
  FLY_MS,
  FLIP_MS,
  PICK_LIFT_PX,
} from './tarotStageGeometry'

// Last landing slot (the structured card's image container) on screen, measured
// live each frame so the flip tracks autoscroll — same selector/last-match rule
// as the legacy useTarotFlyAnimation.
const measureLanding = (): Rect | null => {
  const els = document.querySelectorAll('[data-oracle-card-landing]')
  const el = els.length > 0 ? els[els.length - 1] : null
  if (!el) return null
  const r = el.getBoundingClientRect()
  return {
    top: r.top, left: r.left, width: r.width, height: r.height,
  }
}

// Internal pick sub-states (ported from the legacy TarotPickACard): the user
// clicks → the card lifts (picking) → holds (hanging) → flies (flying). The
// outer FSM enters its `flying` phase when we fire onChooseCard at the
// hanging→flying boundary, so the visual fly and FSM `flying` run concurrently.
type InternalPhase = 'waiting' | 'picking' | 'hanging' | 'flying'

export interface TarotCardStageProps {
  /** FSM phase. The same card elements persist across every animating phase —
   *  `shuffle` (riffle) → `pick_a_card` → `flying` → `jiggle` → `flip` →
   *  `land` — so no boundary unmounts or snaps. */
  phase: TarotPhase
  backImageUrl: string
  /** Front (revealed) card image; mounted on the picked card so it's decoded
   *  before the flip turns it face-up. */
  frontImageUrl?: string
  /** Card-sized, content-area-centered rect the picked card flies to. */
  jiggleTarget: Rect
  /** Fired at the hanging→flying boundary (drives the FSM USER_CHOOSES_CARD). */
  onChooseCard: () => void
  /** Fired when the flip motion completes (drives the FSM FLIP_TIMER → land). */
  onFlipEnd: () => void
}

export const TarotCardStage: React.FC<TarotCardStageProps> = ({
  phase,
  backImageUrl,
  frontImageUrl,
  jiggleTarget,
  onChooseCard,
  onFlipEnd,
}) => {
  const drawYourCard = useTranslatedText({ keys: ['tarotPickACard', 'drawYourCard'] })
  const cardAriaLabelTemplate = useTranslatedText({ keys: ['tarotPickACard', 'cardAriaLabel'] })

  const [viewportWidth, setViewportWidth] = useState(0)
  const [imageDecoded, setImageDecoded] = useState(false)
  const [fanned, setFanned] = useState(false)
  const [flayDone, setFlayDone] = useState(false)
  const [internalPhase, setInternalPhase] = useState<InternalPhase>('waiting')
  const [pickedIndex, setPickedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const layout = getFanLayout(viewportWidth, jiggleTarget)
  const { numCards, cardWidth, cardHeight } = layout

  // Card button elements; the rAF loop writes their transform/opacity.
  const cardElsRef = useRef<(HTMLElement | null)[]>([])
  // Flipper wrappers (one per card); the rAF loop writes their rotateY. Only the
  // picked card actually flips — the rest stay at 0°.
  const flipperElsRef = useRef<(HTMLElement | null)[]>([])
  // Ref on the first rendered card's <img> — decode the ACTUAL element (a
  // throwaway Image() doesn't warm the rendered <img>'s pixel cache; AI-882
  // issue 2). Hold the fan hidden until it resolves so cards pop in painted.
  const firstCardImgRef = useRef<HTMLImageElement>(null)

  // Hold the view→FSM callbacks in refs so the timer effects don't restart when
  // the parent passes new function identities.
  const onChooseCardRef = useRef(onChooseCard)
  useEffect(() => { onChooseCardRef.current = onChooseCard }, [onChooseCard])
  const onFlipEndRef = useRef(onFlipEnd)
  useEffect(() => { onFlipEndRef.current = onFlipEnd }, [onFlipEnd])

  // Baseline for the riffle clock, captured on the first frame so the shuffle
  // starts from a near-stacked pose rather than mid-cycle.
  const shuffleStartRef = useRef<number | null>(null)
  // Jiggle clock baseline; the sine angle is reconstructable from it at any
  // instant, so the flip can read the LIVE angle without the rAF loop's state.
  const jiggleStartRef = useRef<number | null>(null)
  // Flip clock baseline + the live jiggle angle captured the instant flip starts
  // — the flip eases rotateY from THIS angle to 180° (continuous → issue 5).
  const flipStartRef = useRef<number | null>(null)
  const flipStartRotateYRef = useRef(0)

  useEffect(() => {
    setViewportWidth(window.innerWidth)
  }, [])

  // Decode the actual rendered back <img> before revealing the fan (AI-882 issue 2).
  useEffect(() => {
    let cancelled = false
    const img = firstCardImgRef.current
    if (!img) return undefined
    const markDecoded = () => { if (!cancelled) setImageDecoded(true) }
    const { decode } = (img as { decode?: () => Promise<void> })
    if (typeof decode === 'function') {
      decode.call(img).then(markDecoded, markDecoded)
    } else if (img.complete) {
      markDecoded()
    } else {
      img.onload = markDecoded
      img.onerror = markDecoded
    }
    return () => { cancelled = true }
  }, [backImageUrl])

  // Fan out once measured AND decoded AND we've reached the pick phase. Before
  // that (during `shuffle`) the cards riffle; `fanned` latches true at
  // pick_a_card and never reverts for this mount, so the fan-out tween runs once.
  const reachedPick = phase === 'pick_a_card' || phase === 'flying'
  useEffect(() => {
    if (viewportWidth === 0 || !imageDecoded || !reachedPick) return undefined
    const raf = requestAnimationFrame(() => setFanned(true))
    return () => cancelAnimationFrame(raf)
  }, [viewportWidth, imageDecoded, reachedPick])

  // After the fan-out duration, hover transitions use the snappier HOVER_MS.
  useEffect(() => {
    if (!fanned) return undefined
    const timer = global.setTimeout(() => setFlayDone(true), FLAY_MS)
    return () => global.clearTimeout(timer)
  }, [fanned])

  // picking → hanging
  useEffect(() => {
    if (internalPhase !== 'picking') return undefined
    const timer = global.setTimeout(() => setInternalPhase('hanging'), PICK_MS)
    return () => global.clearTimeout(timer)
  }, [internalPhase])

  // hanging → flying: fire onChooseCard the moment the fly begins.
  useEffect(() => {
    if (internalPhase !== 'hanging') return undefined
    const timer = global.setTimeout(() => {
      onChooseCardRef.current()
      setInternalPhase('flying')
    }, HANG_MS)
    return () => global.clearTimeout(timer)
  }, [internalPhase])

  // flip → land: the stage owns the flip's duration, so it tells the FSM when
  // the flip completes. There is no competing hook timer for this edge — the
  // reducer's FLIP_TIMER is driven solely by this callback.
  useEffect(() => {
    if (phase !== 'flip') return undefined
    const timer = global.setTimeout(() => onFlipEndRef.current(), FLIP_MS)
    return () => global.clearTimeout(timer)
  }, [phase])

  // Cards are only interactive during pick_a_card (not while shuffling/flying).
  const interactive = phase === 'pick_a_card' && internalPhase === 'waiting'

  const handleCardClick = useCallback((i: number) => {
    if (!interactive) return
    setHoveredIndex(null)
    setPickedIndex(i)
    setInternalPhase('picking')
  }, [interactive])

  // Per-card scene for the rAF loop. Read through a ref so the loop never
  // restarts on re-render; reassigned every render to capture latest state.
  const computeScene = useCallback((i: number, nowMs: number): CardScene => {
    if (!fanned) {
      // Before the fan-out: riffle the deck as a continuous function of time
      // (durationMs 0 → the loop writes it directly each frame). When `fanned`
      // latches, the fan-out tween starts from this LIVE pose → no snap.
      if (shuffleStartRef.current === null) shuffleStartRef.current = nowMs
      const r = getShuffleOffset(i, layout, nowMs - shuffleStartRef.current)
      return {
        ...r, rotateY: 0, opacity: 1, durationMs: 0, easing: easeOut,
      }
    }
    const isPicked = i === pickedIndex
    const isOther = pickedIndex !== null && !isPicked

    // Jiggle: hold the flying pose and oscillate rotateY as a continuous sine
    // from 0° (durationMs 0 → written directly each frame). Starting at 0 keeps
    // it continuous with the fly's upright end — no -40° snap (issue 4).
    if (isPicked && phase === 'jiggle') {
      if (jiggleStartRef.current === null) jiggleStartRef.current = nowMs
      const o = getFlyingOffset(layout)
      return {
        ...o,
        rotateY: getJiggleRotateY(nowMs - jiggleStartRef.current),
        opacity: 1,
        durationMs: 0,
        easing: easeInOut,
      }
    }

    // Flip (and the frozen `land` hold): ease rotateY from the LIVE jiggle angle
    // to 180° while translating+scaling from center to the re-measured landing
    // slot. Computed directly (durationMs 0) so the per-frame landing re-measure
    // doesn't restart a tween. Continuous from the jiggle angle → no snap (issue
    // 5); clamps at the landing during `land` as the AI-765 anti-flash overlay.
    if (isPicked && (phase === 'flip' || phase === 'land')) {
      if (flipStartRef.current === null) {
        flipStartRef.current = nowMs
        const jiggleElapsed = jiggleStartRef.current !== null ? nowMs - jiggleStartRef.current : 0
        flipStartRotateYRef.current = getJiggleRotateY(jiggleElapsed)
      }
      const p = easeInOut(clamp01((nowMs - flipStartRef.current) / FLIP_MS))
      const flyStart = getFlyingOffset(layout)
      const landing = measureLanding()
      const target = landing
        ? getLandingOffset(layout, landing)
        : {
          dx: flyStart.dx, dy: flyStart.dy, scaleX: 1, scaleY: 1,
        }
      return {
        dx: lerp(flyStart.dx, target.dx, p),
        dy: lerp(flyStart.dy, target.dy, p),
        rotateZ: 0,
        rotateY: lerp(flipStartRotateYRef.current, 180, p),
        opacity: 1,
        scaleX: lerp(1, target.scaleX, p),
        scaleY: lerp(1, target.scaleY, p),
        durationMs: 0,
        easing: easeInOut,
      }
    }

    if (isPicked && internalPhase === 'flying') {
      const o = getFlyingOffset(layout)
      return {
        ...o, rotateY: 0, opacity: 1, durationMs: FLY_MS, easing: easeInOut,
      }
    }
    if (isPicked && (internalPhase === 'picking' || internalPhase === 'hanging')) {
      const o = getPickedLiftOffset(i, layout)
      return {
        ...o, rotateY: 0, opacity: 1, durationMs: PICK_MS, easing: easeOut,
      }
    }
    const hoverFraction = interactive && hoveredIndex === i ? 1 : 0
    const o = getFanOffset(i, layout, hoverFraction)
    // The non-picked cards fade out over PICK_MS (the legacy "picking"
    // transition). Otherwise: FLAY_MS for the initial fan-out, then the
    // snappier HOVER_MS once fanned.
    let durationMs = flayDone ? HOVER_MS : FLAY_MS
    if (isOther) durationMs = PICK_MS
    return {
      ...o,
      rotateY: 0,
      opacity: isOther ? 0 : 1,
      durationMs,
      easing: easeOut,
    }
  }, [fanned, interactive, flayDone, phase, internalPhase, pickedIndex, hoveredIndex, layout])

  const sceneRef = useRef<(i: number, nowMs: number) => CardScene>(computeScene)
  sceneRef.current = computeScene

  useTarotStageAnimation({
    active: true,
    numCards,
    cardEls: cardElsRef,
    flipperEls: flipperElsRef,
    sceneRef,
  })

  // Fan container — fixed at the fan origin; cards are absolutely placed at
  // (-w/2, -h) so transform translate(dx,dy) puts each card's bottom-center at
  // (origin + dx/dy), rotated about that point. Hidden until decoded.
  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    left: layout.fanOriginX,
    top: layout.fanOriginY,
    width: 0,
    height: 0,
    zIndex: 1000,
    opacity: imageDecoded ? 1 : 0,
    transition: 'opacity 120ms ease-out',
  }

  // Label shows only during pick_a_card (not while shuffling/flying); it stays
  // mounted so opacity can animate — fading in with the fan and out on pick.
  const labelStyle: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    top: layout.fanOriginY - cardHeight - PICK_LIFT_PX / 2 - 48,
    width: '100%',
    textAlign: 'center',
    pointerEvents: 'none',
    zIndex: 1001,
    opacity: phase === 'pick_a_card' && pickedIndex === null ? 1 : 0,
    transition: `opacity ${PICK_MS}ms ease-out`,
  }

  // The full-screen backdrop covers the chat only while the deck is on stage
  // (shuffle/pick/flying). It fades out as the chosen card begins to jiggle, so
  // the card flips and lands over the live chat — matching the legacy hand-off
  // where TarotPickACard unmounted at the flying→jiggle boundary.
  const backdropVisible = phase === 'shuffle' || phase === 'pick_a_card' || phase === 'flying'
  const backdropStyle: React.CSSProperties = {
    opacity: backdropVisible ? 1 : 0,
    pointerEvents: backdropVisible ? 'auto' : 'none',
    transition: 'opacity 200ms ease-out',
  }

  return createPortal(
    <>
      <div className="tarot-theater__pick-backdrop" style={backdropStyle} aria-hidden="true" />
      <p className="tarot-theater__pick-label" style={labelStyle}>
        {drawYourCard}
      </p>
      <div
        className="tarot-theater__pick-container"
        style={containerStyle}
        role="group"
        aria-label={drawYourCard}
      >
        {Array.from({ length: numCards }, (_, i) => {
          const ariaLabel = cardAriaLabelTemplate?.replace('{number}', String(i + 1))
            ?? `Card ${i + 1}`
          const isPicked = i === pickedIndex
          const cardStyle: React.CSSProperties = {
            position: 'absolute',
            left: -cardWidth / 2,
            top: -cardHeight,
            width: cardWidth,
            height: cardHeight,
            transformOrigin: 'bottom center',
            cursor: interactive ? 'pointer' : 'default',
            // transform/opacity are written each frame by the rAF loop.
          }
          // Drop the card's drop-shadow once landed so the overlay matches the
          // (unfiltered) structured card it sits on — preserves the AI-765
          // anti-flash at the flip→land hand-off.
          const cardClass = [
            'tarot-theater__pick-card',
            isPicked && phase === 'land' ? 'tarot-theater__pick-card--landed' : '',
          ].filter(Boolean).join(' ')
          return (
            <button
              type="button"
              key={i}
              ref={(el) => { cardElsRef.current[i] = el }}
              className={cardClass}
              style={cardStyle}
              onClick={() => handleCardClick(i)}
              onMouseEnter={() => { if (interactive) setHoveredIndex(i) }}
              onMouseLeave={() => setHoveredIndex(null)}
              disabled={!interactive}
              aria-label={ariaLabel}
            >
              {/* Flipper wrapper carries the 3D rotateY the loop writes; only the
                  picked card flips. The front face is mounted on the picked card
                  as soon as it's known so it decodes before the flip reveals it. */}
              <div
                className="tarot-theater__pick-flipper"
                ref={(el) => { flipperElsRef.current[i] = el }}
              >
                <img
                  ref={i === 0 ? firstCardImgRef : undefined}
                  src={backImageUrl}
                  alt=""
                  draggable={false}
                  decoding="sync"
                  className="tarot-theater__pick-card-img"
                />
                {isPicked && frontImageUrl && (
                  <img
                    src={frontImageUrl}
                    alt=""
                    draggable={false}
                    className="tarot-theater__pick-card-front"
                  />
                )}
              </div>
            </button>
          )
        })}
      </div>
    </>,
    document.body,
  )
}
